import React from "react";
import styled from "styled-components";
import { Card, Tag, Button, Space, Typography, Empty } from "antd";
import { 
  PhoneOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  RightOutlined,
  ExclamationCircleOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { logError } from "../../../shared/utils/logger.js";
import { useAuth } from "../../../contexts/AuthContext";

const { Text } = Typography;

const QueueCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  height: 100%;

  .ant-card-body {
    padding: 16px;
  }
`;

const ActionItem = styled.div`
  padding: 12px;
  border: 1px solid ${props => {
    if (props.$urgent) return "#ffccc7";
    if (props.$overdue) return "#ffccc7";
    return "#f0f0f0";
  }};
  border-radius: 8px;
  margin-bottom: 8px;
  background: ${props => {
    if (props.$urgent) return "#fff1f0";
    if (props.$overdue) return "#fff1f0";
    return "#fff";
  }};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #1890ff;
    box-shadow: 0 2px 4px rgba(24, 144, 255, 0.1);
  }

`;

const ActionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const ActionTitle = styled.div`
  font-weight: 500;
  flex: 1;
`;

const ActionMeta = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 12px;
  margin-top: 8px;
`;

const QuickActions = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 8px;
`;

const ActionQueue = ({ followUps = [], upcomingOrders = [] }) => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canViewFollowUps = hasPermission("followups:view");

  // Combine and prioritize items
  const actionItems = [
    ...(canViewFollowUps
      ? followUps
          .filter((f) => f.status === "overdue" || f.priority === "urgent")
          .map((f) => ({
            type: "followup",
            id: f.id,
            title: f.title,
            entity: f.entityContext?.name || "Unknown",
            priority: f.priority,
            overdue: f.status === "overdue",
            urgent: f.priority === "urgent",
            timeRisk: f.status === "overdue" ? "Overdue" : "Due today",
            entityType: f.entityType,
          }))
      : []),
    ...upcomingOrders
      .filter(o => !o.booking?.moveDate || new Date(o.booking.moveDate) <= new Date(Date.now() + 24 * 60 * 60 * 1000))
      .map(o => ({
        type: "order",
        id: o.id || o._id,
        title: `Order ${o.id || o._id}`,
        entity: o.customer || "Unknown",
        priority: o.priority || "medium",
        overdue: false,
        urgent: !o.booking?.moveDate,
        timeRisk: !o.booking?.moveDate ? "Not scheduled" : "Scheduled",
        entityType: "order",
      })),
  ].sort((a, b) => {
    // Sort by: overdue > urgent > priority > time
    if (a.overdue && !b.overdue) return -1;
    if (!a.overdue && b.overdue) return 1;
    if (a.urgent && !b.urgent) return -1;
    if (!a.urgent && b.urgent) return 1;
    return 0;
  }).slice(0, 10); // Top 10 items

  const handleItemClick = (item) => {
    try {
      if (item.type === "followup") {
        navigate(`/followups/${item.id}`);
      } else {
        navigate(`/orders/${item.id}`);
      }
    } catch (error) {
      logError("Failed to navigate to item", {
        itemType: item.type,
        itemId: item.id,
        error: error.message,
      });
    }
  };

  const handleQuickAction = (action, item, e) => {
    e.stopPropagation();
    try {
      // Implement quick actions (call, WhatsApp, mark as done)
      // TODO: Implement actual quick action handlers
      logError("Quick action not implemented", {
        action,
        itemType: item.type,
        itemId: item.id,
      });
    } catch (error) {
      logError("Failed to execute quick action", {
        action,
        itemType: item.type,
        itemId: item.id,
        error: error.message,
      });
    }
  };

  const renderActionItem = (item) => {
    return (
      <ActionItem
        key={`${item.type}-${item.id}`}
        $overdue={item.overdue}
        $urgent={item.urgent}
        onClick={() => handleItemClick(item)}
      >
        <ActionHeader>
          <ActionTitle>
            <Text strong>{item.title}</Text>
            <ActionMeta>
              <Tag color={item.overdue ? "red" : item.urgent ? "orange" : "blue"}>
                {item.timeRisk}
              </Tag>
              <Tag>{item.entityType?.toUpperCase()}</Tag>
              <Text type="secondary">{item.entity}</Text>
            </ActionMeta>
          </ActionTitle>
          <RightOutlined style={{ color: "#8c8c8c" }} />
        </ActionHeader>
        <QuickActions>
          <Button
            size="small"
            icon={<PhoneOutlined />}
            onClick={(e) => handleQuickAction("call", item, e)}
          >
            Call
          </Button>
          <Button
            size="small"
            icon={<MessageOutlined />}
            onClick={(e) => handleQuickAction("whatsapp", item, e)}
          >
            WhatsApp
          </Button>
          <Button
            size="small"
            icon={<CheckCircleOutlined />}
            onClick={(e) => handleQuickAction("done", item, e)}
          >
            Done
          </Button>
        </QuickActions>
      </ActionItem>
    );
  };

  return (
    <QueueCard
      title={
        <Space>
          <ExclamationCircleOutlined />
          <span>Needs Attention</span>
        </Space>
      }
      extra={
        canViewFollowUps ? (
          <Button
            type="link"
            size="small"
            onClick={() => navigate("/followups")}
          >
            View All
          </Button>
        ) : null
      }
    >
      {actionItems.length > 0 ? (
        <Space direction="vertical" style={{ width: "100%" }}>
          {actionItems.map(renderActionItem)}
        </Space>
      ) : (
        <Empty
          description="No items requiring attention"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}
    </QueueCard>
  );
};

export default ActionQueue;

